import {
  ConstraintConfigurationInput,
  ConstraintDaysOfWeekFragment,
  ConstraintEmailRequiredFragment,
  ConstraintInAudienceFragment,
  ConstraintInStoreOnlyFragment,
  ConstraintLocationInStateFragment,
  ConstraintOrderAheadOnlyFragment,
  ConstraintPaymentInstrumentFragment,
  ConstraintPaymentMethodFragment,
  ConstraintPreAuthFragment,
  ConstraintPurchasableEligibilityFragment,
  ConstraintPurchaseThresholdFragment,
  ConstraintRequiresActivationFragment,
  ConstraintRequiresTransactionVoucherFragment,
  ConstraintRequiresVoucherAvailableForDiscountFragment,
  ConstraintRequiresVoucherFragment,
  ConstraintStoreLocationFragment,
  ConstraintStoreLocationGroupFragment,
  ConstraintSubBrandFragment,
  ConstraintTimeOfDayFragment,
  OfferFragment,
} from '@occc/graphql';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';

export class ConstraintDuplicationService {
  private static throwNotSupportedConstraintError(_constraint: never): never {
    throw new Error('Not supported constraint');
  }

  public copy(constraints: OfferFragment['constraints']) {
    return constraints.map((constraint) => {
      if (constraintHelper.isDaysOfWeek(constraint)) {
        return this.copyDaysOfWeek(constraint);
      }
      if (constraintHelper.isInAudience(constraint)) {
        return this.copyInAudience(constraint);
      }
      if (constraintHelper.isPreAuth(constraint)) {
        return this.copyIsAPreAuthConstraint(constraint);
      }
      if (constraintHelper.isRequiresVoucher(constraint)) {
        return this.copyIsARequiresVoucherConstraint(constraint);
      }
      if (constraintHelper.isRequiresVoucherForDiscount(constraint)) {
        return this.copyIsARequiresVoucherAvailableForDiscountConstraint(constraint);
      }
      if (constraintHelper.isLocationInState(constraint)) {
        return this.copyLocationInState(constraint);
      }
      if (constraintHelper.isPurchaseThreshold(constraint)) {
        return this.copyPurchaseThreshold(constraint);
      }
      if (constraintHelper.isRequiresActivation(constraint)) {
        return this.copyRequiresActivation(constraint);
      }
      if (constraintHelper.isStoreLocation(constraint)) {
        return this.copyStoreLocation(constraint);
      }
      if (constraintHelper.isTimeOfDay(constraint)) {
        return this.copyTimeOfDay(constraint);
      }
      if (constraintHelper.isRequiresTransactionVoucher(constraint)) {
        return this.copyRequiresTransactionVoucher(constraint);
      }
      if (constraintHelper.isPaymentMethod(constraint)) {
        return this.copyPaymentMethod(constraint);
      }
      if (constraintHelper.isStoreLocationGroup(constraint)) {
        return this.copyStoreLocationGroup(constraint);
      }
      if (constraintHelper.isPurchasableEligibility(constraint)) {
        return this.copyPurchasableEligibility(constraint);
      }
      if (constraintHelper.isPaymentInstrument(constraint)) {
        return this.copyPaymentInstrument(constraint);
      }
      if (constraintHelper.isSubBrand(constraint)) {
        return this.copySubBrand(constraint);
      }
      if (constraintHelper.isInStoreOnly(constraint)) {
        return this.copyInStoreOnly(constraint);
      }
      if (constraintHelper.isOrderAheadOnly(constraint)) {
        return this.copyOrderAheadOnly(constraint);
      }
      if (constraintHelper.isEmailRequired(constraint)) {
        return this.copyEmailRequired(constraint);
      }

      // Assert that we've handled all Constraint types.
      // If a new type is added, TypeScript will flag this as an error.
      return ConstraintDuplicationService.throwNotSupportedConstraintError(constraint);
    });
  }

  private copyDaysOfWeek(constraint: ConstraintDaysOfWeekFragment): ConstraintConfigurationInput {
    const { availableWeeklyOnDays, startOfDayTimezone } = constraint;

    return {
      daysOfWeek: {
        availableWeeklyOnDays,
        startOfDayTimezone,
      },
    };
  }

  private copyInAudience(constraint: ConstraintInAudienceFragment): ConstraintConfigurationInput {
    return {
      inAudience: {
        audienceId: constraint.audienceId,
      },
    };
  }

  private copyIsAPreAuthConstraint(
    _constraint: ConstraintPreAuthFragment,
  ): ConstraintConfigurationInput {
    return {
      isAPreAuthConstraint: true,
    };
  }

  private copyIsARequiresVoucherConstraint(
    _constraint: ConstraintRequiresVoucherFragment,
  ): ConstraintConfigurationInput {
    return {
      isARequiresVoucherConstraint: true,
    };
  }

  private copyIsARequiresVoucherAvailableForDiscountConstraint(
    _constraint: ConstraintRequiresVoucherAvailableForDiscountFragment,
  ): ConstraintConfigurationInput {
    return {
      isARequiresVoucherAvailableForDiscountConstraint: true,
    };
  }

  private copyLocationInState(
    constraint: ConstraintLocationInStateFragment,
  ): ConstraintConfigurationInput {
    return {
      locationInState: {
        states: constraint.states,
      },
    };
  }

  private copyPurchaseThreshold(
    constraint: ConstraintPurchaseThresholdFragment,
  ): ConstraintConfigurationInput {
    const { eligibleProductGroupIds, countProductsBy, threshold } = constraint;

    return {
      purchaseThreshold: {
        eligibleProductGroupIds,
        countProductsBy,
        threshold,
      },
    };
  }

  private copyRequiresActivation(
    constraint: ConstraintRequiresActivationFragment,
  ): ConstraintConfigurationInput {
    const { mustFulfillWithinDays, allowMultipleFulfillmentsOnActivation } = constraint;

    return {
      requiresActivation: {
        mustFulfillWithinDays,
        allowMultipleFulfillmentsOnActivation,
      },
    };
  }

  private copyStoreLocation(
    constraint: ConstraintStoreLocationFragment,
  ): ConstraintConfigurationInput {
    const { evaluationMethod, locations } = constraint;

    return {
      storeLocation: {
        evaluationMethod,
        locationIds: locations.map((location) => location.id),
      },
    };
  }

  private copyStoreLocationGroup(
    constraint: ConstraintStoreLocationGroupFragment,
  ): ConstraintConfigurationInput {
    return {
      storeLocationGroup: {
        locationGroupIds: constraint.locationGroups.map((locationGroup) => locationGroup.uuid),
      },
    };
  }

  private copyPurchasableEligibility(
    constraint: ConstraintPurchasableEligibilityFragment,
  ): ConstraintConfigurationInput {
    const { ownershipCondition, voucheredOffers } = constraint;
    return {
      purchasableEligibility: {
        ownershipCondition,
        voucheredOfferIds: voucheredOffers.map((offer) => offer.id),
      },
    };
  }

  private copyTimeOfDay(constraint: ConstraintTimeOfDayFragment): ConstraintConfigurationInput {
    const { availableDailyEndingAtTime, availableDailyStartingAtTime, timezone } = constraint;

    return {
      timeOfDay: {
        availableDailyEndingAtTime,
        availableDailyStartingAtTime,
        timezone,
      },
    };
  }

  private copyRequiresTransactionVoucher(
    constraint: ConstraintRequiresTransactionVoucherFragment,
  ): ConstraintConfigurationInput {
    const { allowSingleOfferVoucherStacking, breakagePointRounding, breakageRefund } = constraint;
    return {
      requiresTransactionVoucherConstraintWithConfiguration: {
        allowSingleOfferVoucherStacking,
        breakagePointRounding,
        breakageRefund,
      },
    };
  }

  private copyPaymentMethod(
    constraint: ConstraintPaymentMethodFragment,
  ): ConstraintConfigurationInput {
    const { paymentMethods, paymentEvaluationMethod } = constraint;

    return {
      paymentMethod: {
        paymentMethods,
        evaluationMethod: paymentEvaluationMethod,
      },
    };
  }

  private copyPaymentInstrument(
    constraint: ConstraintPaymentInstrumentFragment,
  ): ConstraintConfigurationInput {
    const { paymentInstruments, paymentInstrumentEvaluationMethod } = constraint;

    return {
      paymentInstrument: {
        paymentInstruments,
        evaluationMethod: paymentInstrumentEvaluationMethod,
      },
    };
  }

  private copyInStoreOnly(
    _constraint: ConstraintInStoreOnlyFragment,
  ): ConstraintConfigurationInput {
    return {
      isInStoreOnlyConstraint: true,
    };
  }

  private copyOrderAheadOnly(
    _constraint: ConstraintOrderAheadOnlyFragment,
  ): ConstraintConfigurationInput {
    return {
      isOrderAheadOnlyConstraint: true,
    };
  }

  private copyEmailRequired(
    _constraint: ConstraintEmailRequiredFragment,
  ): ConstraintConfigurationInput {
    return {
      isEmailRequired: true,
    };
  }

  private copySubBrand(constraint: ConstraintSubBrandFragment): ConstraintConfigurationInput {
    const { subBrandUuids } = constraint;

    return {
      subBrand: {
        subBrandUuids,
      },
    };
  }
}

export const constraintDuplicationService = new ConstraintDuplicationService();
